import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import parse, { domToReact } from 'html-react-parser';
import _get from 'lodash/get';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useSiteCopySelector from 'components/useSiteCopySelector';
import LazyAnimation from './LazyAnimation';

const useStyles = makeStyles(theme => ({
  section: {
    paddingTop: 91,
    paddingBottom: 83,
    backgroundColor: '#FFFFFF',
    [theme.breakpoints.down('sm')]: {
      paddingTop: 98,
      paddingBottom: 86,
    },
  },
  wrapper: {
    width: '80%',
    maxWidth: 1080,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  h1: {
    fontFamily: 'PoppinsSemiBold',
    fontSize: 30,
    fontWeight: '600',
    textAlign: 'center',
    color: '#333333',
    lineHeight: '42px',
    width: '57.8%',
    marginBottom: 10,
    marginLeft: 'auto',
    marginRight: 'auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  subtitle: {
    fontFamily: 'OpenSansRegular',
    fontWeight: '400',
    fontSize: 18,
    lineHeight: '24px',
    textAlign: 'center',
    width: '57.8%',
    color: '#666666',
    '& > a': {
      color: '#2EA3F2',
      '-webkit-appearance': 'none',
    },
    '& > b': {
      fontFamily: 'OpenSansBold',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 30,
      width: '100%',
    },
  },
  item: {
    paddingBottom: 27,
    paddingTop: 27,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      paddingBottom: 30,
      paddingTop: 30,
    },
  },
  itemReverse: {
    flexDirection: 'row-reverse',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column-reverse',
    },
  },
  itemTextWrapper: {
    width: '47.25%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  itemHeader: {
    fontFamily: 'PoppinsBold',
    fontWeight: '700',
    fontSize: 27,
    lineHeight: '35px',
    color: '#01619B',
    paddingBottom: 10,
  },
  itemBody: {
    fontFamily: 'OpenSansRegular',
    fontSize: 18,
    fontWeight: '500',
    lineHeight: '24px',
    color: '#666666',
    '& > a': {
      color: '#2EA3F2',
      '-webkit-appearance': 'none',
    },
  },
  itemImageWrapper: {
    width: '47.25%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  itemImage: {
    maxWidth: '100%',
    height: 'auto',
    objectFit: 'contain',
  },
  buttonWrapper: {
    marginTop: 25,
  },
}));

function SectionC({ onPageExit }) {
  // const theme = useTheme();
  // const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [solutionsSiteCopy] = useSiteCopySelector(['solutions-homepage']);
  const config = _get(solutionsSiteCopy, 'pageCopy.section-c', {});
  const classes = useStyles();

  const options = {
    replace: domNode => {
      if (domNode.name === 'a') {
        return (
          <a
            role="link"
            tabIndex={0}
            onClick={() => onPageExit(domNode.attribs.href, 'section-c')}
            {...domNode.attribs}
          >
            {domToReact(domNode.children)}
          </a>
        );
      }
      return domToReact(domNode);
    },
  };

  return (
    <section id="section-c">
      <div className={classes.section}>
        <div className={classes.wrapper}>
          <Typography className={classes.h1}>
            {parse(config.title, options)}
          </Typography>
          <Typography className={classes.subtitle}>
            {parse(config.subtitle, options)}
          </Typography>
          {config.items.map((item, index) => (
            <div
              className={_classNames(classes.item, {
                [classes.itemReverse]: index % 2 === 1,
              })}
            >
              <div className={classes.itemTextWrapper}>
                <Typography className={classes.itemHeader}>
                  {parse(item.header, options)}
                </Typography>
                <Typography className={classes.itemBody}>
                  {parse(item.body, options)}
                </Typography>
              </div>
              <div className={classes.itemImageWrapper}>
                <LazyAnimation
                  type={
                    index % 2 === 0
                      ? 'translate-left-200 fade-in'
                      : 'translate-right-200 fade-in'
                  }
                >
                  <img src={item.image} className={classes.itemImage} alt="" />
                </LazyAnimation>
              </div>
            </div>
          ))}
          <div className={classes.buttonWrapper}>
            {parse(config.button, options)}
          </div>
        </div>
      </div>
    </section>
  );
}

SectionC.propTypes = {
  onPageExit: PropTypes.func,
};

export default SectionC;
